import { createFilterOptions, FilterOptionsState } from "@mui/material";
import { optionMatchesSearchQuery } from "../../../utils/optionSearchMatch";
import { isCustomTypeaheadOption } from "./typeaheadOptionSections";

export type TypeaheadFilterConfig<T> = {
  isRemoteSearch: boolean;
  freeOptions: boolean;
  getLabel: (option: T) => string;
  createCreatableOption: (inputValue: string) => T;
  /** When true, append "Add …" even if other options exist (single-select). */
  allowCreatableWhenResultsExist?: boolean;
};

/**
 * Remote typeahead: preserve API sort order, but still filter org custom
 * options against the live input (API results are already server-filtered).
 * Local typeahead: default MUI substring filter.
 */
export function buildTypeaheadFilterOptions<T>({
  isRemoteSearch,
  freeOptions,
  getLabel,
  createCreatableOption,
  allowCreatableWhenResultsExist = true,
}: TypeaheadFilterConfig<T>) {
  return (options: T[], state: FilterOptionsState<T>): T[] => {
    const { inputValue } = state;
    const query = inputValue.trim();

    const filtered = isRemoteSearch
      ? query
        ? options.filter((option) => {
            if (!isCustomTypeaheadOption(option)) return true;
            return optionMatchesSearchQuery(
              option as Record<string, unknown>,
              query,
              (o) => getLabel(o as T),
            );
          })
        : options
      : createFilterOptions<T>()(options, state);

    if (!freeOptions || !query) {
      return filtered;
    }

    const exists = options.some(
      (option) => getLabel(option).toLowerCase() === inputValue.toLowerCase(),
    );

    if (exists) return filtered;
    if (!allowCreatableWhenResultsExist && filtered.length > 0) {
      return filtered;
    }

    return [...filtered, createCreatableOption(inputValue)];
  };
}

export function normalizeTypeaheadSearchQuery(input: string): string {
  return (input ?? "").split(" · ")[0].trim();
}
